import React, {createContext, PropsWithChildren, useContext, useState} from 'react';
import {FormikProvider, useFormik} from 'formik';
import * as Yup from 'yup';
import {FileType} from '../../types';
import {useFileTypesOptionsContext} from '../FileTypesOptionsContext/FileTypesOptionsContext';

type PopupUploadFilesContext = {
  fileTypes: FileType[],
  uploading: boolean,
  files: File[],
  onFilesChange: (files: File[]) => void,
  onFilesEdit: (files: File[]) => void,
  submit: () => Promise<void>,
  isValid: boolean,
  isUploadAllowed: boolean,
  multifile: boolean,
}

interface PopupUploadFilesProviderProps extends PropsWithChildren<{}> {
  onFileUpload: (files: File[], fileTyeID: number) => Promise<void>;
}

type FormikValues = {
  fileTypeID: number,
  files: File[],
}

const defaultContext: PopupUploadFilesContext = {
  fileTypes: [],
  uploading: false,
  files: [],
  onFilesChange: () => {},
  onFilesEdit: () => {},
  submit: async () => {},
  isValid: false,
  isUploadAllowed: false,
  multifile: false,
};

const PopupUploadFilesContext = createContext<PopupUploadFilesContext>(defaultContext);

export const usePopupUploadFilesContext = () => useContext(PopupUploadFilesContext);

export const PopupUploadFilesProvider = ({children, onFileUpload}: PopupUploadFilesProviderProps) => {
  const {fileTypes} = useFileTypesOptionsContext();
  const [uploading, setUploading] = useState(false);
  const formik = useFormik<FormikValues>({
    initialValues: {
      fileTypeID: 0,
      files: []
    },
    validationSchema: Yup.object().shape({
      fileTypeID: Yup.number().required('Name is required').moreThan(0, 'Please select a file type'),
      files: Yup.array().required('File is required').min(1, 'Please select at least one file')
    }),
    onSubmit: () => {},
  });

  const submit = async () => {
    try {
      setUploading(true);
      await onFileUpload(formik.values.files, formik.values.fileTypeID);
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (files: File[]) => {
    const [selectedFileType] = fileTypes.filter(fileType => fileType.value === formik.values.fileTypeID);
    if (selectedFileType) {
      const selectedFiles = formik.values.files.map(file => file.name);
      const uniqueFiles = files.filter(file => !selectedFiles.includes(file.name));
      formik.setFieldValue('files', selectedFileType.multifile ? [...formik.values.files, ...uniqueFiles] : uniqueFiles);
    } else {
      formik.setFieldValue('files', files);
    }
  };

  const handleFileEdit = (files: File[]) => {
    formik.setFieldValue('files', files);
  };

  const multifileAllowed = () => {
    const [selectedFileType] = fileTypes.filter(fileType => fileType.value === formik.values.fileTypeID);
    return selectedFileType?.multifile ?? false;
  };

  return (
    <PopupUploadFilesContext.Provider value={{
      fileTypes,
      uploading,
      files: formik.values.files,
      onFilesChange: handleFileChange,
      onFilesEdit: handleFileEdit,
      submit,
      isValid: formik.isValid,
      isUploadAllowed: formik.values.fileTypeID > 0,
      multifile: multifileAllowed(),
    }}>
      <FormikProvider value={formik}>
        {children}
      </FormikProvider>
    </PopupUploadFilesContext.Provider>
  );
};